<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="My Tasks" class="px-4 pr-5 mb-5">
      </SimplePageHeader>
      <v-row class="px-5 mb-sm-5">
        <v-col cols="12" sm="4" class="d-flex align-center">
          <v-switch
            v-model="showCompleted"
            label="Show completed"
            hide-details
            dense
            class="mt-0"
            @change="handleSearchClick"
          />
        </v-col>
        <v-col cols="12" sm="4">
          <ClientsDropDownList
            :id="clientId"
            :isRequired="false"
            @clientIdChange="clientId = $event"
          />
        </v-col>
        <v-col cols="12" sm="4" class="d-flex align-center">
          <v-btn color="primary" outlined @click="handleSearchClick">Search</v-btn>
          <v-btn class="ml-2" outlined @click="handleClearClick">Clear</v-btn>
        </v-col>
      </v-row>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="workflowTaskList"
          :options.sync="options"
          :server-items-length="serverItemsLength"
          item-key="id"
          class="elevation-2"
        >
          <template v-slot:item.title="{ item }">
            <span>{{ item.title }}</span>
            <v-chip v-if="isAutomatedTask(item)" x-small label color="purple" dark class="ml-1">
              <v-icon x-small left>flash_on</v-icon>{{ item.taskTypeId === workflowTaskType.AutomatedDocument ? 'Document' : 'Email' }}
            </v-chip>
            <div v-if="item.taskTypeId === workflowTaskType.AutomatedDocument && item.outputFileName" class="caption green--text">
              <v-icon x-small color="green">description</v-icon> {{ item.outputFileName }}
            </div>
            <div v-if="isAutomatedTask(item) && item.executionResult" class="caption red--text">
              {{ item.executionResult }}
            </div>
          </template>
          <template v-slot:item.candidateClient="{ item }">
            <span>{{ item.candidateName }}<br><span class="grey--text caption">{{ item.clientName }}</span></span>
          </template>
          <template v-slot:item.statusName="{ item }">
            <v-chip
              x-small
              label
              :color="taskStatusAttrs(item.statusId).color"
              :dark="taskStatusAttrs(item.statusId).dark"
            >{{ item.statusName }}</v-chip>
            <v-chip
              v-if="item.reviewStatusId"
              x-small
              label
              class="ml-1"
              :color="reviewStatusAttrs(item.reviewStatusId).color"
              :dark="reviewStatusAttrs(item.reviewStatusId).dark"
            >{{ item.reviewStatusName }}</v-chip>
          </template>
          <template v-slot:item.dueDate="{ item }">
            <span :class="{
              'past-due':  isDuePast(item),
              'for-today': isDueToday(item),
              'this-week': isDueThisWeek(item),
              'complete':  item.statusId === workflowTaskStatus.Done
            }">{{ item.dueDate | formatDate }}</span>
          </template>
          <template v-slot:item.action="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-if="item.statusId !== workflowTaskStatus.Done && item.statusId !== workflowTaskStatus.Blocked && item.statusId !== workflowTaskStatus.Cancelled && item.taskTypeId !== workflowTaskType.AutomatedEmail"
                  class="mx-1 my-1"
                  color="green"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="openDoneConfirmation(item)"
                >
                  <v-icon color="green">check_circle</v-icon>
                </v-btn>
              </template>
              <span>Mark as Done</span>
            </v-tooltip>
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click="openEditDialog(item)"
            >
              <v-icon color="primary">edit</v-icon>
            </v-btn>
            <v-btn
              v-if="canReExecute(item)"
              class="mx-1 my-1"
              color="orange darken-1"
              outlined
              :loading="reExecutingTaskId === item.id"
              @click="handleReExecute(item)"
            >
              <v-icon color="orange darken-1">flash_on</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>

    <WorkflowTaskEditDialog
      v-model="showEditDialog"
      :taskId="editingTaskId"
      @saved="handleTaskSaved"
    />

    <ConfirmationModal
      title="Mark Task as Done?"
      :contentText="taskToMarkDone ? `Are you sure you want to mark '${taskToMarkDone.title}' as done?` : ''"
      :showModal="showDoneConfirmation"
      toolbarColor="success"
      okText="Yes"
      cancelText="No"
      :loading="confirmingDone"
      @ok-click="handleMarkDoneConfirmed"
      @cancel-click="showDoneConfirmation = false; taskToMarkDone = null"
    />

    <BottomSpacer></BottomSpacer>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { BottomSpacer } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { ClientsDropDownList } from '@/components/clients'
import WorkflowTaskEditDialog from './WorkflowTaskEditDialog'
import ConfirmationModal from '@/components/dialogs/ConfirmationModal'
import { userHasRoles, getCurrentUser } from '@/lib/authentication'
import { ADMIN, SUPER_ADMIN } from '@/lib/constants/securityRoles'
import workflowStatusMixin from './workflowStatusMixin'
import workflowEmailReviewStatusMixin from '@/components/workflowEmailSends/workflowEmailReviewStatusMixin'
import workflowTaskAutomationMixin from './workflowTaskAutomationMixin'
import { notifySuccess, notifyError } from '@/lib/notifications'
import { workflowTaskStatus } from '@/lib/constants/workflowStatuses'
import { workflowTaskType } from '@/lib/constants/workflowEmailStatuses'
import { parseGridOptionsFromQuery, buildPaginationQueryParams, isAllRecordsSortOnlyChange, setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  mixins: [workflowStatusMixin, workflowEmailReviewStatusMixin, workflowTaskAutomationMixin],
  components: {
    BottomSpacer,
    SimplePageHeader,
    ClientsDropDownList,
    WorkflowTaskEditDialog,
    ConfirmationModal
  },
  data () {
    return {
      workflowTaskStatus,
      workflowTaskType,
      showEditDialog: false,
      editingTaskId: null,
      taskToMarkDone: null,
      showDoneConfirmation: false,
      confirmingDone: false,
      reExecutingTaskId: null,
      hasFetchedAllTasks: false,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['dueDate'],
        sortDesc: [false]
      },
      headers: [
        { text: 'Title', value: 'title', sortable: false },
        { text: 'Candidate / Client', value: 'candidateClient', sortable: false, width: '20%' },
        { text: 'Due Date', value: 'dueDate', align: 'center', width: '12%' },
        { text: 'Status', value: 'statusName', align: 'center', sortable: false, width: '12%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '15%' }
      ]
    }
  },
  computed: {
    ...mapFields('workflowTasks', ['workflowTaskList', 'totalRecords']),
    ...mapFields('workflowTaskFilters', ['clientId', 'showCompleted']),
    isAdmin () {
      return userHasRoles({ user: getCurrentUser(), allowedRoles: [ADMIN, SUPER_ADMIN] })
    },
    serverItemsLength () {
      return this.options.itemsPerPage === -1 ? undefined : this.totalRecords
    }
  },
  watch: {
    options: {
      handler (newOptions, previousOptions) {
        this.syncOptionsToQuery()
        if (!this.shouldFetchTasks(newOptions, previousOptions)) return
        this.fetchTasks()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions('workflowTasks', ['getWorkflowTasksWithFiltersPaginated', 'getWorkflowTasksWithFilters']),
    ...mapActions('workflowTaskFilters', ['clearFilters']),
    openEditDialog (task) {
      this.editingTaskId = task.id
      this.showEditDialog = true
    },
    canReExecute (task) {
      return this.isAdmin && this.isAutomatedTask(task) && task.statusId === workflowTaskStatus.OffTrack
    },
    async handleReExecute (task) {
      this.reExecutingTaskId = task.id
      try {
        await this.$store.dispatch('workflowTask/reExecuteTask', task.id)
        this.loadMyTasks()
      } catch (e) {
        // error already notified by store
      } finally {
        this.reExecutingTaskId = null
      }
    },
    openDoneConfirmation (task) {
      this.taskToMarkDone = task
      this.showDoneConfirmation = true
    },
    async handleMarkDoneConfirmed () {
      this.confirmingDone = true
      try {
        const response = await this.$store.dispatch('workflowTask/quickUpdateStatus', {
          task: this.taskToMarkDone,
          statusId: workflowTaskStatus.Done
        })
        if (!response.data) {
          throw new Error(response.errorMessage || 'Unable to mark task as done.')
        }
        notifySuccess(this.$store.dispatch, 'Task marked as done')
        this.showDoneConfirmation = false
        this.taskToMarkDone = null
        this.loadMyTasks()
      } catch (error) {
        notifyError(this.$store.dispatch, error || 'Unable to mark task as done. Try again later.')
      } finally {
        this.confirmingDone = false
      }
    },
    handleTaskSaved () {
      this.loadMyTasks()
    },
    handleSearchClick () {
      this.options = { ...this.options, page: 1 }
      this.syncOptionsToQuery()
    },
    handleClearClick () {
      const userId = getCurrentUser().id
      this.clearFilters(userId)
      this.handleSearchClick()
    },
    loadMyTasks () {
      this.fetchTasks()
    },
    getOptionsFromQuery () {
      return parseGridOptionsFromQuery(this.$route.query, this.options)
    },
    applyFiltersFromQuery () {
      const { clientId, showCompleted } = this.$route.query
      const parsedClientId = Number(clientId)
      this.clientId = Number.isInteger(parsedClientId) && parsedClientId > 0 ? parsedClientId : null
      this.showCompleted = showCompleted === 'true'
    },
    syncOptionsToQuery () {
      const { sortColumn, sortDirection, ...paginationParams } = buildPaginationQueryParams(this.options)
      const nextQuery = { ...this.$route.query, ...paginationParams }
      const userId = getCurrentUser().id

      setOrDeleteQueryParam(nextQuery, 'sortColumn', sortColumn)
      setOrDeleteQueryParam(nextQuery, 'sortDirection', sortColumn ? sortDirection : null)
      setOrDeleteQueryParam(nextQuery, 'assignedToUserId', userId)
      setOrDeleteQueryParam(nextQuery, 'clientId', this.clientId)
      setOrDeleteQueryParam(nextQuery, 'showCompleted', this.showCompleted ? 'true' : 'false')

      if (hasQueryChanged(this.$route.query, nextQuery, ['pageNumber', 'pageSize', 'sortColumn', 'sortDirection', 'assignedToUserId', 'clientId', 'showCompleted'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    fetchTasks () {
      const { page, itemsPerPage, sortBy, sortDesc } = this.options
      const userId = getCurrentUser().id

      if (itemsPerPage === -1) {
        this.getWorkflowTasksWithFilters().then(tasks => {
          this.hasFetchedAllTasks = true
          this.workflowTaskList = tasks
        })
        return
      }

      this.hasFetchedAllTasks = false
      this.getWorkflowTasksWithFiltersPaginated({
        assignedToUserId: userId,
        pageNumber: page,
        pageSize: itemsPerPage,
        sortColumn: sortBy && sortBy.length ? sortBy[0] : null,
        sortDirection: sortDesc && sortDesc.length ? (sortDesc[0] ? 'DESC' : 'ASC') : null
      }).then(tasks => {
        this.workflowTaskList = tasks
      })
    },
    shouldFetchTasks (newOptions, previousOptions) {
      return !isAllRecordsSortOnlyChange(newOptions, previousOptions, this.hasFetchedAllTasks)
    },
    isDuePast (task) {
      return task.dueDate && new Date(task.dueDate) < new Date() && task.statusId !== workflowTaskStatus.Done
    },
    isDueToday (task) {
      if (!task.dueDate || task.statusId === workflowTaskStatus.Done) return false
      return new Date(task.dueDate).setHours(0, 0, 0, 0) === new Date().setHours(0, 0, 0, 0)
    },
    isDueThisWeek (task) {
      if (!task.dueDate || task.statusId === workflowTaskStatus.Done) return false
      const due = new Date(task.dueDate)
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const in7 = new Date(today); in7.setDate(in7.getDate() + 7)
      return due > today && due <= in7
    }
  },
  created () {
    const userId = getCurrentUser().id
    this.$store.commit('workflowTaskFilters/updateField', { path: 'assignedToUserId', value: userId })
    this.applyFiltersFromQuery()
    this.options = {
      ...this.options,
      ...this.getOptionsFromQuery()
    }
    this.syncOptionsToQuery()
  }
}
</script>

<style>
.past-due {
  color: white;
  border: 1px solid red;
  background-color: red;
  border-radius: 10px;
  padding: 1px 15px 1px 15px;
  display: block;
  text-align: center;
}

.for-today {
  color: black;
  letter-spacing: 1px;
  border: 1px solid orange;
  background-color: orange;
  border-radius: 10px;
  padding: 1px 15px 1px 15px;
  display: block;
  text-align: center;
}

.this-week {
  color: black;
  border: 1px solid #f5a623;
  background-color: #ffe59e;
  border-radius: 10px;
  padding: 1px 15px;
  display: block;
  text-align: center;
}

.complete {
  color: rgb(64, 153, 47);
  border: 1px solid rgb(64, 153, 47);
  border-radius: 10px;
  padding: 1px 15px 1px 15px;
  display: block;
  text-align: center;
}
</style>
